import { api } from "./api";

export interface Mentor {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  avatar?: string;
  title?: string;
  bio?: string;
  specialty?: string;
  company?: string;
  expertise?: string[];
  pricePerSession?: number;
  rating?: number;
  reviewsCount?: number;
  studentsCount?: number;
  sessionsCount?: number;
  responseTime?: string;
  languages?: string[];
  isVerified?: boolean;
  isTopMentor?: boolean;
  createdAt?: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
  role: "student" | "mentor" | "instructor" | "admin";
  isVerified: boolean;
  isActive: boolean;
  isBlocked?: boolean;
  blockedReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export const adminApi = {
  // Get all users
  getUsers: (options?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    isVerified?: boolean;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.search) params.append("search", options.search);
    if (options?.role) params.append("role", options.role);
    if (options?.isVerified !== undefined) params.append("isVerified", String(options.isVerified));
    if (options?.isActive !== undefined) params.append("isActive", String(options.isActive));
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortOrder) params.append("sortOrder", options.sortOrder);

    return api.get<{ success: boolean; data: User[]; pagination: any }>(
      `/admin/users${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Get user by ID
  getUserById: (userId: string) => {
    return api.get<{ success: boolean; data: User }>(`/admin/users/${userId}`);
  },

// Update user
  updateUser: (userId: string, data: Partial<User>) => {
    return api.put<{ success: boolean; data: User }>(
      `/admin/users/${userId}`,
      data
    );
  },

  // Get all contacts
  getContacts: (query?: string) => {
    return api.get<{ success: boolean; data: any[]; pagination: any }>(
      `/contact${query || ""}`
    );
  },

  // Get contact by ID
  getContactById: (id: string) => {
    return api.get<{ success: boolean; data: any }>(`/contact/${id}`);
  },

  // Reply to contact
  replyToContact: (id: string, replyMessage: string) => {
    return api.put<{ success: boolean; message: string; data: any }>(
      `/contact/${id}/reply`,
      { replyMessage }
    );
  },

  // Update contact status
  updateContactStatus: (id: string, status: string) => {
    return api.put<{ success: boolean; message: string; data: any }>(
      `/contact/${id}/status`,
      { status }
    );
  },

  // Delete contact
  deleteContact: (id: string) => {
    return api.delete<{ success: boolean; message: string }>(`/contact/${id}`);
  },

  // Delete user
  deleteUser: (userId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/admin/users/${userId}`);
  },

  // Toggle user active status
  toggleUserActive: (userId: string) => {
    return api.put<{ success: boolean; message: string }>(`/admin/users/${userId}/toggle-active`);
  },

  // Ban user
  banUser: (userId: string, reason: string) => {
    return api.post<{ success: boolean; message: string }>(`/admin/users/${userId}/ban`, { reason });
  },

  // Unban user
  unbanUser: (userId: string) => {
    return api.post<{ success: boolean; message: string }>(`/admin/users/${userId}/unban`);
  },

  // Verify user
  verifyUser: (userId: string) => {
    return api.put<{ success: boolean; message: string }>(`/admin/users/${userId}/verify`);
  },

  // Change user role
  changeUserRole: (userId: string, role: string) => {
    return api.put<{ success: boolean; message: string }>(`/admin/users/${userId}/role`, { role });
  },

  // Get dashboard stats
  getStats: () => {
    return api.get<{
      success: boolean;
      data: {
        totalUsers: number;
        totalStudents: number;
        totalInstructors: number;
        totalMentors: number;
        totalAdmins: number;
        verifiedUsers: number;
        unverifiedUsers: number;
        activeUsers: number;
        inactiveUsers: number;
      };
    }>("/admin/users/stats/users");
  },

  // Create user
  createUser: (data: { firstName: string; lastName: string; email: string; role: string; password: string }) => {
    return api.post<{ success: boolean; message: string; data: User }>("/admin/users", data);
  },

  // Security actions
  deleteAllUsers: () => {
    return api.delete<{ success: boolean; message: string }>("/admin/security/delete-all-users");
  },

  deleteAllCourses: () => {
    return api.delete<{ success: boolean; message: string }>("/admin/security/delete-all-courses");
  },

  clearDatabase: () => {
    return api.delete<{ success: boolean; message: string }>("/admin/security/clear-database");
  },

  resetDatabase: () => {
    return api.delete<{ success: boolean; message: string }>("/admin/security/reset-database");
  },

  // Activity logs
  getActivityLogs: (options?: {
    page?: number;
    limit?: number;
    action?: string;
    severity?: string;
    isSuspicious?: boolean;
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.action) params.append("action", options.action);
    if (options?.severity) params.append("severity", options.severity);
    if (options?.isSuspicious !== undefined) params.append("isSuspicious", String(options.isSuspicious));
    if (options?.startDate) params.append("startDate", options.startDate);
    if (options?.endDate) params.append("endDate", options.endDate);

    return api.get<{
      success: boolean;
      data: any[];
      summary: any;
      pagination: any;
    }>(`/activity-logs${params.toString() ? `?${params.toString()}` : ""}`);
  },

  getSuspiciousActivities: (options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));

    return api.get<{ success: boolean; data: any[]; pagination: any }>(
      `/activity-logs/suspicious${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  getPlatformAnalytics: (days?: number) => {
    return api.get<{ success: boolean; data: any }>(
      `/activity-logs/analytics${days ? `?days=${days}` : ""}`
    );
  },

  flagActivity: (activityId: string, reason: string) => {
    return api.post<{ success: boolean; message: string; data: any }>(
      `/activity-logs/${activityId}/flag`,
      { reason }
    );
  },

  deleteActivityLogs: (ids?: string[], olderThan?: number, action?: string) => {
    return api.delete<{ success: boolean; message: string }>("/activity-logs/bulk", {
      ids,
      olderThan,
      action,
    });
  },

  // Mentors
  getMentors: (options?: {
    page?: number;
    limit?: number;
    search?: string;
    verified?: boolean;
    topMentor?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.search) params.append("search", options.search);
    if (options?.verified !== undefined) params.append("verified", String(options.verified));
    if (options?.topMentor !== undefined) params.append("topMentor", String(options.topMentor));
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortOrder) params.append("sortOrder", options.sortOrder);

    return api.get<{ success: boolean; data: Mentor[]; pagination: any }>(
      `/admin/mentors${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  getMentorById: (mentorId: string) => {
    return api.get<{ success: boolean; data: any }>(`/admin/mentors/${mentorId}`);
  },

  updateMentor: (mentorId: string, data: Partial<Mentor>) => {
    return api.put<{ success: boolean; message: string; data: any }>(
      `/admin/mentors/${mentorId}`,
      data
    );
  },

  deleteMentor: (mentorId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/admin/mentors/${mentorId}`);
  },

  cleanupOrphanedUserData: (dryRun = true) => {
    return api.post<{ success: boolean; message: string; data: any }>(
      `/admin/cleanup-orphaned-data`,
      { dryRun }
    );
  },
};